import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { TenantSeatLimits, TenantSummary } from '@bliks-io/contracts';
import { Button, Card, CardContent, CardHeader, CardTitle, Label } from '@bliks-io/ui';

import { useUpdateTenant } from '@/pages/admin/api/mutations/useTenants';
import { useTenantSeatUsage } from '@/pages/admin/api/queries/useTenantSeatUsage';
import { SEAT_POOLS, formatSeatLimit, type ParsedSeatLimit } from '@/lib/seat-limits';
import { SeatLimitInput } from './SeatLimitInput';

interface TenantSeatLimitsCardProps {
  tenant: TenantSummary;
}

/**
 * Per-role seat caps of one tenant. Owns its save and dirty state rather than
 * riding the settings tab's button, because it writes the TENANT through
 * `PATCH /api/v2/tenants/:tenantId` while the rest of the tab writes the
 * tenant's ORGANIZATION through a different endpoint.
 */
export function TenantSeatLimitsCard({ tenant }: TenantSeatLimitsCardProps) {
  const { t } = useTranslation();
  const { data: usage } = useTenantSeatUsage(tenant._id);
  const updateTenant = useUpdateTenant();

  // The inputs own their draft text; this mirrors only what each draft parses
  // to, which is all the save gate below needs.
  const [edited, setEdited] = useState<Partial<Record<keyof TenantSeatLimits, ParsedSeatLimit>>>(
    {}
  );

  const edits = SEAT_POOLS.map((pool) => {
    const stored = tenant.seatLimits?.[pool.limitField] ?? null;
    return {
      ...pool,
      stored,
      parsed: edited[pool.limitField] ?? { valid: true as const, limit: stored }
    };
  });

  const allValid = edits.every(({ parsed }) => parsed.valid);

  /**
   * Only the pools this page actually changed, cleared ones as an explicit
   * `null`. The backend writes each pool as its own dot path, so an omitted pool
   * is left untouched — sending the untouched ones back would replay whatever
   * this page last read and silently revert a cap another superadmin raised in
   * the meantime. `null` still has to be explicit: it is what clears a cap.
   */
  const changedLimits = edits.reduce<TenantSeatLimits>(
    (changes, { limitField, stored, parsed }) => {
      if (parsed.valid && parsed.limit !== stored) changes[limitField] = parsed.limit;
      return changes;
    },
    {}
  );

  const hasChanges = Object.keys(changedLimits).length > 0;

  const handleSave = async () => {
    try {
      await updateTenant.mutateAsync({ id: tenant._id, data: { seatLimits: changedLimits } });
      setEdited({});
      toast.success(t('adminSettings.tenant.saved'));
    } catch {
      toast.error(t('adminSettings.tenant.saveFailed'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="w-5 h-5 text-muted-foreground" />
          {t('seatLimits.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {edits.map(({ limitField, usageKey, labelKey }) => {
          const pool = usage?.[usageKey];
          return (
            <div key={limitField} className="flex items-center justify-between gap-4">
              <Label htmlFor={limitField}>{t(labelKey)}</Label>
              <div className="flex items-center gap-3">
                {pool && (
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {pool.used} / {formatSeatLimit(pool.limit)}
                  </span>
                )}
                <SeatLimitInput
                  id={limitField}
                  stored={tenant.seatLimits?.[limitField]}
                  onParsed={(parsed) =>
                    setEdited((current) => ({ ...current, [limitField]: parsed }))
                  }
                  className="w-28"
                />
              </div>
            </div>
          );
        })}

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            loading={updateTenant.isPending}
            disabled={!hasChanges || !allValid}
          >
            <Save className="w-4 h-4 mr-2" />
            {t('common.Save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
